// Drives the staff-list bulk selection: per-row checkboxes, a current-page
// select-all, and a floating "Selected Staff" widget. The selection is a
// Map(id -> {name, surname}) mirrored to localStorage (keyed per user) so it
// survives pagination. Download builds a transient POST form carrying the CSRF
// token and one user_ids[] field per id, submits it (bypassing Turbo so the CSV
// downloads without navigating), then clears the selection.

import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "row", "selectAll", "downloadButton", "downloadCount",
    "widget", "widgetCount", "list", "uploadModal"
  ]
  static values = { storageKey: String, downloadUrl: String }

  connect() {
    this.selected = this.load()
    this.syncRows()
    this.render()
  }

  toggleRow(event) {
    const checkbox = event.target
    this.setSelected(checkbox.checked, checkbox.dataset)
    this.persist()
    this.render()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.rowTargets.forEach((checkbox) => {
      checkbox.checked = checked
      this.setSelected(checked, checkbox.dataset)
    })
    this.persist()
    this.render()
  }

  removeOne(event) {
    const id = event.currentTarget.dataset.id
    this.selected.delete(id)
    const row = this.rowTargets.find((checkbox) => checkbox.dataset.id === id)
    if (row) row.checked = false
    this.persist()
    this.render()
  }

  clear() {
    this.selected.clear()
    this.rowTargets.forEach((checkbox) => { checkbox.checked = false })
    this.persist()
    this.render()
  }

  download() {
    const ids = [...this.selected.keys()]
    if (ids.length === 0) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.downloadUrlValue
    form.setAttribute("data-turbo", "false")
    form.style.display = "none"

    const csrf = document.querySelector('meta[name="csrf-token"]')
    if (csrf) form.appendChild(this.hiddenInput("authenticity_token", csrf.content))
    ids.forEach((id) => form.appendChild(this.hiddenInput("user_ids[]", id)))

    document.body.appendChild(form)
    form.submit()
    form.remove()
    this.clear()
  }

  openUpload() {
    if (this.hasUploadModalTarget) this.uploadModalTarget.showModal()
  }

  // --- private ---

  setSelected(isSelected, dataset) {
    if (isSelected) {
      this.selected.set(dataset.id, { name: dataset.name, surname: dataset.surname })
    } else {
      this.selected.delete(dataset.id)
    }
  }

  hiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  syncRows() {
    this.rowTargets.forEach((checkbox) => {
      checkbox.checked = this.selected.has(checkbox.dataset.id)
    })
  }

  syncSelectAll() {
    if (!this.hasSelectAllTarget) return
    const rows = this.rowTargets
    this.selectAllTarget.checked = rows.length > 0 && rows.every((checkbox) => checkbox.checked)
  }

  render() {
    const count = this.selected.size

    if (this.hasDownloadButtonTarget) {
      // Tailwind 1.7.6 has no disabled: variant, so the dim is driven here (gotcha #44).
      this.downloadButtonTarget.disabled = count === 0
      this.downloadButtonTarget.style.opacity = count === 0 ? "0.5" : "1"
      this.downloadButtonTarget.style.cursor = count === 0 ? "not-allowed" : "pointer"
    }
    if (this.hasDownloadCountTarget) this.downloadCountTarget.textContent = `[${count}]`
    if (this.hasWidgetCountTarget) this.widgetCountTarget.textContent = count
    if (this.hasWidgetTarget) this.widgetTarget.classList.toggle("hidden", count === 0)

    if (this.hasListTarget) {
      this.listTarget.innerHTML = ""
      this.selected.forEach((person, id) => this.listTarget.appendChild(this.listItem(id, person)))
    }

    this.syncSelectAll()
  }

  listItem(id, person) {
    const item = document.createElement("li")
    item.className = "flex items-center justify-between py-1"

    const label = document.createElement("span")
    label.textContent = `${person.name || ""} ${person.surname || ""}`.trim()
    item.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-gray-400 hover:text-red-600 ml-2 leading-none"
    remove.textContent = "×"
    remove.dataset.id = id
    remove.dataset.action = "click->bulk-user-selection#removeOne"
    item.appendChild(remove)

    return item
  }

  load() {
    try {
      const raw = window.localStorage.getItem(this.storageKeyValue)
      return raw ? new Map(Object.entries(JSON.parse(raw))) : new Map()
    } catch {
      return new Map()
    }
  }

  persist() {
    try {
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify(Object.fromEntries(this.selected)))
    } catch {
      // localStorage unavailable — the selection simply won't survive pagination.
    }
  }
}
